import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import FileDropZone from '../components/commun/FileDropZone';
import axios from 'axios'; // <-- On va faire la requête directe ici
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
import { isLegrandUser } from '../utils/userUtils';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
}

const FormBahrein: React.FC = () => {
  // Récupération des paramètres depuis react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États issus des paramètres de route
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // États spécifiques à ce formulaire
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);

  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Champs supplémentaires (employeur)
  const [employeurNom, setEmployeurNom] = useState<string>('');
  const [employeurAdresse, setEmployeurAdresse] = useState<string>('');
  const [employeurCodePostal, setEmployeurCodePostal] = useState<string>('');
  const [employeurVille, setEmployeurVille] = useState<string>('');

  // Calcul du total (frais + supplément assurance)
  const total =
    prixconsulat +
    prixintervention +
    (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction pour parser une date au format jj/mm/aaaa
  const parseDate = (dateStr: string): Date | null => {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return null;
  };

  // Validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);
    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push("La date de départ doit être une date valide et postérieure à aujourd'hui.");
      }
    } else {
      errors.push("La date de départ est requise.");
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push("La date de retour doit être une date valide et postérieure à la date de départ.");
      }
    } else if (dateSortie && !sortieDate) {
      errors.push("La date de retour est requise et doit être au format valide.");
    }

    if (!selectedVoyageur) errors.push('Voyageur non sélectionné');
    if (!dateDepart.trim()) errors.push('Date de départ');
    if (!dateSortie.trim()) errors.push('Date de sortie');
    if (!employeurNom.trim()) errors.push("Nom de l'employeur");
    if (!employeurAdresse.trim()) errors.push("Adresse de l'employeur");
    if (!employeurCodePostal.trim()) errors.push("Code postal de l'employeur");
    if (!employeurVille.trim()) errors.push("Ville de l'employeur");
    if (!billetAvionFile) errors.push("Billet d’avion aller-retour nominatif requis");
    if (!reservationHotelFile) errors.push("Réservation hôtel ou certificat d’hébergement requis");
    if (!assuranceVisa.trim()) errors.push('Assurance Visa');
    if (!sliderValue) errors.push("Engagement sur les conditions générales de vente");

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  // Revalidation à chaque modification
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    employeurNom,
    employeurAdresse,
    employeurCodePostal,
    employeurVille,
    billetAvionFile,
    reservationHotelFile,
  ]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(
    (v) => v.id.toString() === selectedVoyageur
  );

  // Gestion d'une éventuelle sortie de page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleBack = () => {
    if (
      window.confirm(
        "Les informations saisies ne sont pas enregistrées. Êtes-vous sûr de quitter ce formulaire ?"
      )
    ) {
      navigate(-1);
    }
  };

  // Formatage de la date saisie (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Récupération de l'ID utilisateur
      const userJson = localStorage.getItem('user');
      let userId = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', String(userId));
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);
      formData.append('employeurNom', employeurNom);
      formData.append('employeurAdresse', employeurAdresse);
      formData.append('employeurCodePostal', employeurCodePostal);
      formData.append('employeurVille', employeurVille);

      // On ajoute les fichiers (champ clé -> "billetAvion" etc.)
      if (billetAvionFile) {
        formData.append('billetAvion', billetAvionFile);
      }
      if (reservationHotelFile) {
        formData.append('reservationHotel', reservationHotelFile);
      }

      // Envoi au backend
      const response = await axios.post(
        `${API_BASE_URL}/validerDemande`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setEmployeurNom('');
    setEmployeurAdresse('');
    setEmployeurCodePostal('');
    setEmployeurVille('');
    setBilletAvionFile(null);
    setReservationHotelFile(null);
  };

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistrée avec succès !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {title} a bien été enregistrée.
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Créer une nouvelle demande
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              Formulaire pour {title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Retour
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur (si non en mode édition) */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">Sélectionner un voyageur</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label="Sélectionner un voyageur"
                  onChange={(e) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((voyageur) => (
                    <MenuItem key={voyageur.id} value={voyageur.id.toString()}>
                      {voyageur.nom} {voyageur.prenom}
                      {voyageur.numeropax ? ` - Passeport: ${voyageur.numeropax}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Créer un nouveau voyageur
                </Button>
              </Box>
            </>
          )}

          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt="Avatar"
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label="Date de départ de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date de retour de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs de l'employeur */}
          <TextField
            label="Nom de l’employeur"
            placeholder="Nom de l’employeur"
            value={employeurNom}
            onChange={(e) => setEmployeurNom(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Adresse de l’employeur"
            placeholder="Adresse de l’employeur"
            value={employeurAdresse}
            onChange={(e) => setEmployeurAdresse(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Code postal de l’employeur"
            placeholder="Code postal de l’employeur"
            value={employeurCodePostal}
            onChange={(e) => setEmployeurCodePostal(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Ville de l’employeur"
            placeholder="Ville de l’employeur"
            value={employeurVille}
            onChange={(e) => setEmployeurVille(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Upload du billet d'avion */}
          <FileDropZone
            label="Billet d’avion aller-retour nominatif"
            file={billetAvionFile}
            onFileSelected={setBilletAvionFile}
            required
          />

          {/* Upload de la réservation hôtel */}
          <FileDropZone
            label="Réservation hôtel ou certificat d’hébergement"
            file={reservationHotelFile}
            onFileSelected={setReservationHotelFile}
            required
          />

          {/* AssuranceVisaComponent */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {!hideFeesForLegrand && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">Frais de visa : {prixconsulat}€</Typography>
              <Typography variant="body1">Frais de service : {prixintervention}€</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total : {total}€
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  Formulaire incomplet.
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">Formulaire complet</Typography>
            )}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : () => {}}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                Valider ma demande
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormBahrein;
